/*
 * SQUADZ — Kit Degustação grátis — sections/sq-kit-degustacao.liquid
 *
 * 1. Pegar o kit: intercepta os formulários [data-sk-form]. Garante 1 unidade no carrinho
 *    (adiciona se não houver, reduz para 1 se houver mais) e segue para data-sk-retorno, que já
 *    passa pelo /discount/CUPOM quando a seção tem cupom. Sem JS, o formulário funciona sozinho.
 * 2. Entradas: IntersectionObserver marca [data-sk-anim] com .is-in.
 * 3. Barra fixa (celular): aparece depois que o botão da abertura sai da tela e fica sempre acima
 *    da barra de navegação do tema (mobile-dock).
 * 4. Mede o cabeçalho fixo do tema (--sk-topo) para os títulos com sticky de "Como preparar" e
 *    "Perguntas" pararem logo abaixo dele.
 */
(() => {
  const reduzir = window.matchMedia('(prefers-reduced-motion: reduce)');
  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };

  const lerCarrinho = () => fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

  function mostrarErro(raiz, texto) {
    const caixa = raiz.querySelector('[data-sk-erro]');
    if (!caixa) return;
    caixa.textContent = texto;
    caixa.hidden = false;
    clearTimeout(caixa._t);
    caixa._t = setTimeout(() => { caixa.hidden = true; }, 6000);
  }

  function textoDoErro(e) {
    if (e && typeof e.description === 'string' && e.description) return e.description;
    if (e && typeof e.message === 'string' && e.message) return e.message;
    return 'Não deu para adicionar o kit agora. Tente de novo em instantes.';
  }

  /* ---------------- 1. pegar o kit (sempre 1 unidade) ---------------- */
  async function pegarKit(raiz, botao) {
    const variante = Number(raiz.dataset.skVariante);
    const retorno = raiz.dataset.skRetorno || '/checkout';
    if (!variante) return;
    botao.setAttribute('aria-busy', 'true');
    try {
      const carrinho = await lerCarrinho();
      const linhas = carrinho.items.filter((i) => i.variant_id === variante);
      const total = linhas.reduce((s, i) => s + i.quantity, 0);
      if (total === 0) {
        const r = await fetch('/cart/add.js', { method: 'POST', headers: jsonHeaders, body: JSON.stringify({ items: [{ id: variante, quantity: 1 }] }) });
        const corpo = await r.json();
        if (!r.ok || corpo.status) throw corpo;
      } else if (total > 1) {
        // fica só a primeira linha, com 1 unidade
        for (let k = 0; k < linhas.length; k++) {
          await fetch('/cart/change.js', { method: 'POST', headers: jsonHeaders, body: JSON.stringify({ id: linhas[k].key, quantity: k === 0 ? 1 : 0 }) });
        }
      }
      window.location.href = retorno;
    } catch (e) {
      botao.removeAttribute('aria-busy');
      mostrarErro(raiz, textoDoErro(e));
    }
  }

  /* ---------------- 2. entradas ---------------- */
  function entradas(raiz) {
    const itens = raiz.querySelectorAll('[data-sk-anim]');
    if (!('IntersectionObserver' in window)) { itens.forEach((el) => el.classList.add('is-in')); return; }
    const io = new IntersectionObserver((lista) => {
      for (const it of lista) {
        if (it.isIntersecting) { it.target.classList.add('is-in'); io.unobserve(it.target); }
      }
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    itens.forEach((el) => io.observe(el));
  }

  /* ---------------- 3. barra fixa ---------------- */
  function barraFixa(raiz) {
    const barra = raiz.querySelector('[data-sk-barra]');
    const principal = raiz.querySelector('[data-sk-cta-principal]');
    if (!barra || !principal || !('IntersectionObserver' in window)) return;
    barra.hidden = false;

    /* O tema tem uma barra de navegação fixa no rodapé do celular (mobile-dock). Ela aparece
       por classes, não por posição: cabeçalho com .header-hidden, seção do dock com .active, ou o
       próprio dock com .active ao chegar no fim da página (mobile-dock.js / mobile-dock.css).
       Ler essas classes dá o estado final na hora, sem medir no meio da transição — medir era o
       que falhava perto da chamada final, onde o dock entra pelo fim da página. */
    const dock = document.querySelector('.mobile-dock');
    const secaoDock = dock && dock.closest('.mobile-dock-section');
    const cabecalho = document.querySelector('.header-section');
    const celular = window.matchMedia('(max-width: 767px)');
    const dockAberto = () => {
      if (!dock || !celular.matches || getComputedStyle(dock).display === 'none') return false;
      return (cabecalho && cabecalho.classList.contains('header-sticky') && cabecalho.classList.contains('header-hidden'))
        || (secaoDock && (secaoDock.classList.contains('active') || secaoDock.classList.contains('shopify-active')))
        || dock.classList.contains('active');
    };
    const posicionar = () => raiz.style.setProperty('--sk-dock', dockAberto() ? dock.offsetHeight + 'px' : '0px');
    posicionar();
    if (dock) {
      const obs = new MutationObserver(posicionar);
      [dock, secaoDock, cabecalho].forEach((el) => { if (el) obs.observe(el, { attributes: true, attributeFilter: ['class'] }); });
    }
    window.addEventListener('resize', posicionar, { passive: true });

    new IntersectionObserver(([it]) => {
      barra.classList.toggle('is-visivel', !it.isIntersecting && it.boundingClientRect.top < 0);
    }).observe(principal);
  }

  /* ---------------- topo fixo do tema ---------------- */
  // o cabeçalho do tema fica fixo no alto; os títulos com sticky param logo abaixo dele
  function medirTopo(raiz) {
    const medir = () => {
      let base = 0;
      document.querySelectorAll('.shopify-section-group-header-group').forEach((h) => {
        if (getComputedStyle(h).position !== 'sticky' && getComputedStyle(h).position !== 'fixed') return;
        const r = h.getBoundingClientRect();
        if (r.height < window.innerHeight * 0.4) base = Math.max(base, r.height);
      });
      raiz.style.setProperty('--sk-topo', base + 'px');
    };
    medir();
    window.addEventListener('resize', medir, { passive: true });
  }

  /* ---------------- tabela nutricional em painel ---------------- */
  function tabelas(raiz) {
    const paineis = [...raiz.querySelectorAll('.sk-sabor__tabela')];
    paineis.forEach((det) => {
      det.querySelectorAll('tbody tr').forEach((tr) => {
        if (/^\s*prote/i.test(tr.cells[0] ? tr.cells[0].textContent : '')) tr.classList.add('sk-tab__destaque');
      });
      det.addEventListener('toggle', () => {
        if (det.open) paineis.forEach((o) => { if (o !== det) o.open = false; });
      });
    });
    raiz.addEventListener('click', (ev) => {
      const fechar = ev.target.closest('[data-sk-fechar]');
      if (!fechar) return;
      const det = fechar.closest('details');
      det.open = false;
      const s = det.querySelector('summary');
      if (s) s.focus();
    });
    document.addEventListener('keydown', (ev) => {
      if (ev.key !== 'Escape') return;
      paineis.forEach((d) => { if (d.open) { d.open = false; d.querySelector('summary').focus(); } });
    });
  }

  function iniciar(raiz) {
    if (raiz.dataset.skPronto) return;
    raiz.dataset.skPronto = '1';
    tabelas(raiz);
    medirTopo(raiz);
    const movimento = !reduzir.matches && !raiz.classList.contains('sk--estatico');
    if (movimento) {
      raiz.classList.add('sk--js');
      entradas(raiz);
    }
    raiz.addEventListener('submit', (ev) => {
      const form = ev.target.closest('[data-sk-form]');
      if (!form) return;
      ev.preventDefault();
      pegarKit(raiz, form.querySelector('button'));
    });
    barraFixa(raiz);
  }

  const todas = () => document.querySelectorAll('[data-sk]').forEach(iniciar);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', todas);
  else todas();
  // editor de temas: a seção é recarregada sem recarregar a página
  document.addEventListener('shopify:section:load', (e) => e.target.querySelectorAll('[data-sk]').forEach(iniciar));
})();
